import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent, trackPageview } from "@/lib/analytics";

/**
 * Mount once inside <BrowserRouter>. Fires:
 *   - `pageview` on every client-side route change (Plausible only auto-tracks the initial load)
 *   - `click` for any element carrying `data-analytics-event="..."` (plus optional `data-analytics-*` props)
 */
export default function AnalyticsTracker() {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastPath.current === path) return;
    lastPath.current = path;
    trackPageview(path);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const el = target?.closest<HTMLElement>("[data-analytics-event]");
      if (!el) return;
      const name = el.dataset.analyticsEvent;
      if (!name) return;
      const props: Record<string, string> = {};
      for (const [k, v] of Object.entries(el.dataset)) {
        if (k === "analyticsEvent" || !k.startsWith("analytics") || v == null) continue;
        // dataset.analyticsLocation -> "location"
        const key = k.replace(/^analytics/, "");
        props[key.charAt(0).toLowerCase() + key.slice(1)] = v;
      }
      trackEvent(name, props);
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true } as EventListenerOptions);
  }, []);

  return null;
}